import React, { useContext, useEffect, useState, useRef } from 'react';
import * as PropTypes from 'prop-types';
import { isEmpty, filter } from 'lodash-es';

import RecordStatus from './RecordStatus';
import ModelStatus from './ModelStatus';
import Legend from './Legend';
import Select from 'react-select';
import Spinner from './Spinner';
import { RecordStatusFilter } from '../lib/RecordStatusFilter';
import { Button } from 'datocms-react-ui';
import 'datocms-react-ui/styles.css';
import {
  DatoApiFacadeContext,
  TranslationStatusFactory,
  DatoRecordsFactory,
  RecordFieldsFactory,
  Utilities,
  ModularFieldFactory,
  CLANG
} from '@polestar/content-plugin-core';

const blackListPaths = ['Plugin Development'];

ModelView.propTypes = {
  model: PropTypes.object,
  plugin: PropTypes.object
};

export const filterStatusOptions = [
  { value: 'all', label: 'all' },
  { value: 'Everything is approved', label: 'Everything is approved' },
  { value: 'Waiting for translations from CBG', label: 'Waiting for translations from CBG' },
  { value: 'Waiting for review', label: 'Waiting for review' },
  { value: 'Fields are missing translation(s)', label: 'Fields are missing translation(s)' }
];

function ModelView(props) {
  const { plugin, model } = props;

  const { api } = useContext(DatoApiFacadeContext);

  const allRecords = useRef([]);
  const [records, setRecords] = useState();
  const [statusFilter, setStatusFilter] = useState({ label: 'all', value: 'all' });
  const [loading, setLoading] = useState(true);
  const locales = ['all', ...plugin.site.attributes.locales];
  const localeOptions = getOptions(locales);
  const [selectedLocale, setSelectedLocale] = useState({ label: 'all', value: 'all' });

  function getOptions(locales) {
    const options = [];
    for (const locale of locales) {
      options.push({ label: l, value: l });
    }
    return options;
  }

  function getRecordsWithTranslationData(records) {
    return records.filter((record) => record.translationData && !isEmpty(record.translationData));
  }

  function filterRecords(records) {
    const filteredRecords = getRecordsWithTranslationData(records);
    return filteredRecords;
  }

  async function populateFieldsForRecords(records) {
    const menuItems = await api.getMenuItems();
    //add breadcrumbs, fields and parse json for all records
    const modularFieldFactory = new ModularFieldFactory(api);
    for (const record of records) {
      const modularFieldTree = await modularFieldFactory.getNestedModularFieldTree(model, record, CLANG);
      const recordFields = await RecordFieldsFactory.getRecordFields(record, model.fields, modularFieldTree, api);
      record.breadcrumbs = Utilities.getBredCrumbsForRecord(record.getItemType(), menuItems);
      record.titleField = Utilities.getTitleFieldFromModel(model, record);
      record.translationStatus = TranslationStatusFactory.buildTranslationStatusFromFields(
        plugin.site.attributes.locales,
        record.translationData,
        recordFields
      );
    }
    return records;
  }

  function onLocaleChange(selectedLocaleOption) {
    setSelectedLocale(selectedLocaleOption);
  }

  function onStatusFilterChange(statusFilter) {
    setStatusFilter(statusFilter);
  }

  async function setupRecords() {
    await api.populateModel(model);
    const records = await DatoRecordsFactory.getDatoRecordsFromApi(api, model, model.fields);
    const filteredRecords = filterRecords(records);
    await populateFieldsForRecords(filteredRecords, undefined);
    return filter(
      records,
      (x) =>
        !(
          ['prod'].includes(process.env.STAGE_NAME) &&
          x.breadcrumbs.find((breadcrumb) => blackListPaths.includes(breadcrumb.split('/')[0]))
        )
    );
  }

  function getRecordIds() {
    const getRecordIds = `[${records.map((record) => record.id.toString())}]`;
    navigator.clipboard.writeText(getRecordIds);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const fetchRecords = async () => {
      allRecords.current = await setupRecords();
      setRecords(allRecords.current);
      setLoading(false);
    };
    if (isEmpty(records)) {
      fetchRecords();
    }
  }, []);

  useEffect(() => {
    const recordStatusFilter = RecordStatusFilter(allRecords.current, selectedLocale.value);
    console.log('changing status');
    const filteredRecords = recordStatusFilter[statusFilter.value];
    filteredRecords
      ? undefined
      : console.log('Warning, selected filter/locale combination was not found, returning all records');
    setRecords(filteredRecords ? filteredRecords : []);
  }, [selectedLocale, statusFilter]);

  return (
    <div>
      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="segment">
            <ModelStatus model={model} allRecords={allRecords.current} locale={selectedLocale.value} />
          </div>
          <div>
            <Legend />
            <Select
              id="record-select"
              options={localeOptions}
              onChange={onLocaleChange}
              value={selectedLocale}
              getOptionLabel={(localeOptions) => localeOptions.label}
              getOptionValue={(localeOptions) => localeOptions.value}
            />
            <div className="spacer" type="horizontal" style={{ height: '15px' }} />
            {selectedLocale && (
              <Select
                id="record-select"
                options={filterStatusOptions}
                onChange={onStatusFilterChange}
                value={statusFilter}
                getOptionLabel={(filterStatusOptions) => filterStatusOptions.label}
                getOptionValue={(filterStatusOptions) => filterStatusOptions.value}
              />
            )}
            <div className="spacer" type="horizontal" style={{ height: '15px' }} />
            <Button buttonType="primary" onClick={getRecordIds}>
              Copy record(s) ids to clipboard
            </Button>
            {records.map((record) => {
              return <RecordStatus key={record.id} record={record} plugin={plugin} allLocales={locales} />;
            })}
            {records.length === 0 && <div className="placeholder">No records to show</div>}
          </div>
        </div>
      )}
    </div>
  );
}

export default ModelView;
